<template>
  <div class="min-h-screen bg-gray-50 flex" dir="rtl">
    <!-- Mobile Menu Toggle -->
    <button
      @click="toggleSidebar"
      class="lg:hidden fixed top-4 right-4 z-50 p-2 bg-white rounded-lg shadow-lg flex items-center justify-center"
    >
      <Menu class="w-5 h-5" />
    </button>

    <!-- Sidebar -->
    <div
      class="sidebar fixed lg:static inset-y-0 right-0 z-40 w-80 bg-white shadow-lg h-screen overflow-y-auto transform transition-transform duration-300 ease-in-out"
      :class="sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'"
    >
      <!-- Mobile Close Button -->
      <button
        @click="toggleSidebar"
        class="lg:hidden absolute top-4 left-4 p-2 text-gray-500 hover:text-gray-700"
      >
        <X class="w-5 h-5" />
      </button>
      <div class="p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-6 text-right">الأدوات والفلاتر</h2>
        
        <!-- AI Search Section -->
        <div class="mb-8">
          <h3 class="text-md font-medium text-gray-700 mb-4 flex items-center justify-end">
            <Search class="w-4 h-4 ml-2" />
            شرح المواد بالذكاء الاصطناعي
          </h3>
          <div class="space-y-3">
            <textarea
              v-model="aiQuery"
              placeholder="اسأل عن أي مقال... (مثال: 'اشرح المادة 15')"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-right"
              rows="3"
            ></textarea>
            <button
              @click="handleAISearch"
              :disabled="!aiQuery.trim() || isAILoading"
              class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <BrainCircuit v-if="!isAILoading" class="w-4 h-4 ml-2" />
              <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
              {{ isAILoading ? 'جاري المعالجة...' : 'اشرح بالذكاء الاصطناعي' }}
            </button>
          </div>
          
          <!-- AI Response -->
          <div v-if="aiResponse" class="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div class="flex items-start justify-between mb-2">
              <button
                @click="clearAIResponse"
                class="text-blue-600 hover:text-blue-800"
              >
                <X class="w-4 h-4" />
              </button>
              <h4 class="text-sm font-medium text-blue-900">شرح الذكاء الاصطناعي</h4>
            </div>
            <p class="text-sm text-blue-800 whitespace-pre-wrap text-right">{{ aiResponse }}</p>
          </div>
        </div>

        <!-- Filters Section -->
        <div class="mb-8">
          <h3 class="text-md font-medium text-gray-700 mb-4 flex items-center justify-end">
            <Filter class="w-4 h-4 ml-2" />
            فلاتر المقالات
          </h3>
          
          <!-- Category Filter -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-600 mb-2 text-right">الفئة</label>
            <select
              v-model="selectedCategory"
              @change="applyFilters"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            >
              <option value="">جميع الفئات</option>
              <option v-for="category in availableCategories" :key="category" :value="category">
                {{ category }}
              </option>
            </select>
          </div>

          <!-- Article Number Range -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-600 mb-2 text-right">نطاق أرقام المواد</label>
            <div class="space-y-2">
              <input
                v-model="articleRangeStart"
                @input="applyFilters"
                type="text"
                placeholder="من"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
              />
              <input
                v-model="articleRangeEnd"
                @input="applyFilters"
                type="text"
                placeholder="إلى"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
              />
            </div>
          </div>

          <!-- Content Keywords -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-600 mb-2 text-right">كلمات مفتاحية للمحتوى</label>
            <input
              v-model="contentKeywords"
              @input="applyFilters"
              type="text"
              placeholder="البحث في المحتوى..."
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            />
          </div>

          <!-- Sort Options -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-600 mb-2 text-right">ترتيب حسب</label>
            <select
              v-model="sortBy"
              @change="applyFilters"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
            >
              <option value="articleNumber">رقم المادة</option>
              <option value="category">الفئة</option>
              <option value="createdAt">تاريخ الإنشاء</option>
            </select>
          </div>

          <!-- Clear Filters -->
          <button
            @click="clearFilters"
            class="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center justify-center"
          >
            <RefreshCw class="w-4 h-4 ml-2" />
            مسح الفلاتر
          </button>
        </div>

        <!-- Statistics -->
        <div class="mb-8">
          <h3 class="text-md font-medium text-gray-700 mb-4 flex items-center justify-end text-right">
            <BarChart3 class="w-4 h-4 ml-2" />
            الإحصائيات
          </h3>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">إجمالي المواد:</span>
              <span class="font-medium">{{ law?.articles?.length || 0 }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">النتائج المصفاة:</span>
              <span class="font-medium">{{ filteredArticles.length }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">الفئات:</span>
              <span class="font-medium">{{ availableCategories.length }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 py-6">
          <div class="flex items-center justify-end">
            <h1 class="text-2xl font-bold text-gray-900 mr-4">
              تفاصيل القانون
            </h1>
            <button
              @click="handleBack"
              class="flex items-center text-gray-600 hover:text-gray-900 py-2"
            >
              <span>عودة</span>
              <ArrowLeft class="w-5 h-5 mr-2 rotate-180" />
            </button>
          </div>
        </div>
      </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <div class="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <p class="text-gray-600 text-lg font-medium">يرجى الانتظار...</p>
        <p class="text-gray-500 text-sm mt-2">جاري تحميل بيانات القانون</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <FileText class="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p class="text-gray-500">خطأ في تحميل تفاصيل القانون</p>
        <p class="text-red-600">{{ error }}</p>
        <button
          @click="handleBack"
          class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          العودة للمكتبة
        </button>
      </div>
    </div>

    <!-- Not Found State -->
    <div v-else-if="!law" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <FileText class="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p class="text-gray-500">القانون غير موجود</p>
        <button
          @click="handleBack"
          class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          العودة للمكتبة
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Content -->
      <div class="max-w-7xl mx-auto px-4 py-6">
        <div class="bg-white rounded-lg shadow">
          <div class="p-6">
            <div class="mb-6">
              <h2 class="text-xl font-semibold text-gray-900 mb-4 text-right">
                {{ law.title }}
                <span v-if="law.lawNumber"> - القانون {{ law.lawNumber }}</span>
              </h2>
              
              <div class="mb-4">
                <div class="flex items-center space-x-reverse space-x-4 mb-4 justify-end">
                  <span v-if="law.articles" class="text-sm text-blue-600">
                    {{ law.articles.length }} مادة
                  </span>
                  <span class="text-sm text-gray-500">
                    {{ new Date(law.createdAt).toLocaleDateString('ar-SA') }}
                  </span>
                  <span class="text-sm font-medium text-gray-600">
                    {{ law.category || 'غير مصنف' }}
                  </span>
                </div>
              </div>
              
              <!-- Law Content -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-3 text-right">محتوى القانون</h3>
                <div class="bg-gray-50 rounded-lg p-4">
                  <p class="text-gray-700 whitespace-pre-wrap text-right">
                    {{ law.content || 'No content available' }}
                  </p>
                </div>
              </div>
              
                            
              <!-- Articles Data Table -->
              <div v-if="law.articles && law.articles.length > 0">
                <div class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-900 mb-4 text-right">المواد</h3>
                  
                  <!-- Search and Pagination Controls -->
                  <div class="mb-4 flex flex-col sm:flex-row gap-4">
                    <div class="flex-1">
                      <input
                        v-model="searchQuery"
                        @input="handleSearchChange"
                        type="text"
                        placeholder="البحث في المواد..."
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                      />
                    </div>
                    <div class="flex items-center space-x-reverse space-x-2 justify-end">
                      <select
                        v-model="pageLimit"
                        @change="handleLimitChange"
                        class="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                      >
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <label class="text-sm text-gray-600">عرض:</label>
                    </div>
                  </div>

                  <!-- Articles Table - Responsive Design -->
                  <div class="overflow-x-auto">
                    <div v-if="paginatedArticles.length > 0">
                      <!-- Desktop Table View -->
                      <div class="hidden lg:block">
                        <table class="w-full">
                          <thead class="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                المادة
                              </th>
                              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                المحتوى
                              </th>
                              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                التصنيف
                              </th>
                              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                التاريخ
                              </th>
                            </tr>
                          </thead>
                          <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="article in paginatedArticles" :key="article.id" class="hover:bg-gray-50">
                              <td class="px-6 py-4 whitespace-nowrap">
                                <div class="flex items-center justify-end">
                                  <span class="text-sm font-medium text-gray-900">
                                    المادة {{ article.articleNumber }}
                                  </span>
                                  <FileText class="w-4 h-4 text-blue-500 mr-2" />
                                </div>
                              </td>
                              <td class="px-6 py-4">
                                <div class="text-sm text-gray-900 max-w-md">
                                  <p 
                                    class="whitespace-pre-wrap break-words"
                                    :class="{
                                      'max-h-none overflow-visible': expandedArticles.has(article.id),
                                      'line-clamp-4 max-h-24 overflow-hidden': !expandedArticles.has(article.id)
                                    }"
                                    v-html="searchQuery ? highlightText(article.content, searchQuery) : article.content"
                                  ></p>
                                  <button 
                                    v-if="article.content.length > 150"
                                    @click="toggleArticleExpansion(article.id)"
                                    class="text-blue-600 hover:text-blue-800 text-xs mt-2 flex items-center gap-1 bg-blue-50 px-3 py-2 rounded-md transition-colors"
                                  >
                                    <ChevronUp v-if="expandedArticles.has(article.id)" class="w-4 h-3" />
                                    <ChevronDown v-else class="w-4 h-3" />
                                    {{ expandedArticles.has(article.id) ? 'قراءة أقل' : 'قراءة المزيد' }}
                                  </button>
                                </div>
                              </td>
                              <td class="px-6 py-4 whitespace-nowrap">
                                <span class="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                                  {{ article.category || 'غير مصنف' }}
                                </span>
                              </td>
                              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {{ new Date(article.createdAt).toLocaleDateString('ar-SA') }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Mobile Card View -->
                      <div class="lg:hidden space-y-4 p-4">
                        <div v-for="article in paginatedArticles" :key="article.id" class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          <!-- Article Header -->
                          <div class="flex items-center justify-between mb-3">
                            <div class="flex items-center justify-end">
                              <span class="text-sm font-medium text-gray-900">
                                المادة {{ article.articleNumber }}
                              </span>
                              <FileText class="w-4 h-4 text-blue-500 mr-2" />
                            </div>
                            <span class="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                              {{ article.category || 'غير مصنف' }}
                            </span>
                          </div>
                        
                          <!-- Article Content -->
                          <div class="mb-3">
                            <p 
                              class="text-sm text-gray-900 whitespace-pre-wrap break-words"
                              :class="{
                                'max-h-none overflow-visible': expandedArticles.has(article.id),
                                'line-clamp-4 max-h-24 overflow-hidden': !expandedArticles.has(article.id)
                              }"
                              v-html="searchQuery ? highlightText(article.content, searchQuery) : article.content"
                            ></p>
                            <button 
                              v-if="article.content.length > 150"
                              @click="toggleArticleExpansion(article.id)"
                              class="text-blue-600 hover:text-blue-800 text-xs mt-2 flex items-center gap-1 bg-blue-50 px-3 py-2 rounded-md transition-colors"
                            >
                              <ChevronUp v-if="expandedArticles.has(article.id)" class="w-4 h-3" />
                              <ChevronDown v-else class="w-4 h-3" />
                              {{ expandedArticles.has(article.id) ? 'قراءة أقل' : 'قراءة المزيد' }}
                            </button>
                          </div>
                        
                          <!-- Article Footer -->
                          <div class="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                            <span>
                              {{ new Date(article.createdAt).toLocaleDateString('ar-SA') }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center py-12">
                      <FileText class="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p class="text-gray-500">
                        {{ searchQuery ? 'لا توجد نتائج للبحث' : 'لا توجد مواد في هذا القانون' }}
                      </p>
                    </div>
                  </div>

                  <!-- Pagination -->
                  <div v-if="pagination.totalPages > 1" class="px-4 py-4 border-t border-gray-200">
                    <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div class="text-sm text-gray-700 order-2 sm:order-1">
                        <span class="block sm:hidden">
                          {{ pagination.currentPage }} of {{ pagination.totalPages }}
                        </span>
                        <span class="hidden sm:block">
                          {{ Math.min((pagination.currentPage - 1) * pagination.perPage + 1, pagination.total) }} to 
                          {{ Math.min(pagination.currentPage * pagination.perPage, pagination.total) }} of 
                          {{ pagination.total }} articles
                        </span>
                      </div>
                      <div class="flex items-center justify-center sm:justify-end gap-1 sm:gap-2 order-1 sm:order-2 w-full sm:w-auto">
                        <!-- Previous button -->
                        <button
                          @click="handlePageChange(pagination.currentPage - 1)"
                          :disabled="!pagination.hasPrev"
                          class="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronDown class="w-4 h-4 rotate-90" />
                        </button>
                        
                        <!-- Page numbers -->
                        <template v-for="page in getPaginationPages()" :key="page">
                          <button
                            @click="handlePageChange(page)"
                            :disabled="page === '...'"
                            :class="`px-3 py-1 text-sm border rounded-md ${
                              page === pagination.currentPage
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            } disabled:opacity-50`"
                          >
                            {{ page }}
                          </button>
                        </template>
                        
                        <!-- Next button -->
                        <button
                          @click="handlePageChange(pagination.currentPage + 1)"
                          :disabled="!pagination.hasNext"
                          class="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronDown class="w-4 h-4 -rotate-90" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Embedding Display -->
      <div v-if="embedding" class="max-w-7xl mx-auto px-4 py-6">
        <div class="bg-white rounded-lg shadow">
          <div class="p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4 text-right">
              متجه التضمين
            </h3>
            <div class="bg-gray-100 rounded-lg p-4">
              <p class="text-sm text-gray-600 font-mono break-all text-right">
                {{ embedding }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="max-w-7xl mx-auto px-4 py-6">
        <div class="bg-white rounded-lg shadow">
          <div class="flex justify-center space-x-reverse space-x-4 p-6">
            <button
              @click="downloadLaw"
              class="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Download class="w-5 h-5 ml-2" />
              تحميل
            </button>
            
            <button
              @click="handleBack"
              class="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              <ArrowLeft class="w-5 h-5 ml-2 rotate-180" />
              عودة
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiService } from '../services/api'
import type { LibraryDocument } from '../types'
import { ArrowLeft, FileText, Download, Search, BrainCircuit, Filter, X, RefreshCw, BarChart3, Menu, ChevronUp, ChevronDown } from 'lucide-vue-next'

// Route and router
const route = useRoute()
const router = useRouter()

// Reactive state
const law = ref<LibraryDocument | null>(null)
const embedding = ref<string>('')
const isLoading = ref(true)
const error = ref<string>('')
const currentPage = ref(1)
const searchQuery = ref('')
const pageLimit = ref(20)
const filteredArticles = ref<any[]>([])

// Sidebar state
const aiQuery = ref('')
const aiResponse = ref('')
const isAILoading = ref(false)
const selectedCategory = ref('')
const articleRangeStart = ref('')
const articleRangeEnd = ref('')
const contentKeywords = ref('')
const sortBy = ref('articleNumber')
const sidebarOpen = ref(false)

// Article expansion state
const expandedArticles = ref(new Set())

// Computed properties
const paginatedArticles = computed(() => {
  const startIndex = (currentPage.value - 1) * pageLimit.value
  const endIndex = startIndex + pageLimit.value
  return filteredArticles.value.slice(startIndex, endIndex)
})

// Toggle article expansion
const toggleArticleExpansion = (articleId: string) => {
  const newSet = new Set(expandedArticles.value)
  if (newSet.has(articleId)) {
    newSet.delete(articleId)
  } else {
    newSet.add(articleId)
  }
  expandedArticles.value = newSet
}

// Highlight search text
const highlightText = (text: string, highlight: string) => {
  if (!highlight) return text
  const regex = new RegExp(`(${highlight})`, 'gi')
  return text.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>')
}

// Get pagination pages
const getPaginationPages = () => {
  const pages = []
  const maxButtons = 5
  let startPage = Math.max(1, pagination.value.currentPage - Math.floor(maxButtons / 2))
  let endPage = Math.min(pagination.value.totalPages, startPage + maxButtons - 1)

  if (endPage - startPage < maxButtons - 1) {
    startPage = Math.max(1, endPage - maxButtons + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
}

const availableCategories = computed(() => {
  if (!law.value?.articles) return []
  const categories = new Set(law.value.articles.map(article => article.category).filter(Boolean))
  return Array.from(categories).sort()
})

const pagination = computed(() => {
  const total = filteredArticles.value.length
  const totalPages = Math.ceil(total / pageLimit.value)
  
  return {
    currentPage: currentPage.value,
    perPage: pageLimit.value,
    total,
    totalPages,
    hasNext: currentPage.value < totalPages,
    hasPrev: currentPage.value > 1
  }
})

// Methods
const fetchLawDetails = async () => {
  const id = route.params.id as string
  if (!id) return
  
  isLoading.value = true
  error.value = ''
  
  try {
    console.log('Fetching law details for ID:', id)
    
    const response = await apiService.getLawByID(id)
    
    console.log('Raw API Response:', response)
    console.log('Response type:', typeof response)
    console.log('Response keys:', Object.keys(response || {}))
    
    // Handle response structure - extract data from LawDetailsResponse
    let lawData: LibraryDocument
    
    // The API returns LawDetailsResponse with { success: true, data: LibraryDocument }
    if (response && response.data) {
      console.log('Extracting data from response')
      lawData = response.data
    } else if (response && typeof response === 'object') {
      console.log('Using direct response structure')
      lawData = response as unknown as LibraryDocument
    } else {
      console.error('Invalid response structure:', response)
      throw new Error('Invalid response structure from API')
    }
    
    console.log('Processed law data:', lawData)
    console.log('Law title:', lawData?.title)
    console.log('Law content length:', lawData?.content?.length || 0)
    console.log('Total articles count:', lawData?.articles?.length || 0)
    
    law.value = lawData
    
    // Convert embedding to string for display (if it exists)
    const lawWithEmbedding = lawData as any
    if (lawWithEmbedding.embedding) {
      const embeddingStr = JSON.stringify(lawWithEmbedding.embedding)
      embedding.value = embeddingStr
    }
  } catch (err: any) {
    console.error('Failed to fetch law details:', err)
    console.error('Error details:', {
      message: err?.message,
      stack: err?.stack,
      response: err?.response
    })
    error.value = 'Failed to load law details'
  } finally {
    isLoading.value = false
  }
}

const handlePageChange = (page: number) => {
  console.log('Page change requested to:', page)
  currentPage.value = page
}

const handleSearchChange = () => {
  console.log('Search change to:', searchQuery.value)
  currentPage.value = 1 // Reset to first page when searching
  updateFilteredArticles()
}

const handleLimitChange = () => {
  console.log('Limit change to:', pageLimit.value)
  currentPage.value = 1 // Reset to first page when changing limit
  updateFilteredArticles()
}

const updateFilteredArticles = () => {
  if (!law.value?.articles) {
    filteredArticles.value = []
    return
  }
  
  console.log('Updating filtered articles')
  console.log('Total articles:', law.value.articles.length)
  console.log('Search query:', searchQuery.value)
  
  let filtered = law.value.articles
  
  // Apply main search filter
  if (searchQuery.value) {
    filtered = filtered.filter(article => 
      article.content.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      article.articleNumber.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  }
  
  // Apply sidebar filters
  if (selectedCategory.value) {
    filtered = filtered.filter(article => article.category === selectedCategory.value)
  }
  
  if (contentKeywords.value) {
    const keywords = contentKeywords.value.toLowerCase().split(' ')
    filtered = filtered.filter(article => 
      keywords.every(keyword => article.content.toLowerCase().includes(keyword))
    )
  }
  
  if (articleRangeStart.value || articleRangeEnd.value) {
    filtered = filtered.filter(article => {
      const articleNum = parseInt(article.articleNumber.replace(/[^0-9]/g, '')) || 0
      const start = parseInt(articleRangeStart.value.replace(/[^0-9]/g, '')) || 0
      const end = parseInt(articleRangeEnd.value.replace(/[^0-9]/g, '')) || Infinity
      return articleNum >= start && articleNum <= end
    })
  }
  
  // Apply sorting
  filtered.sort((a, b) => {
    switch (sortBy.value) {
      case 'articleNumber':
        return a.articleNumber.localeCompare(b.articleNumber, undefined, { numeric: true })
      case 'category':
        return (a.category || '').localeCompare(b.category || '')
      case 'createdAt':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      default:
        return 0
    }
  })
  
  console.log('Final filtered articles count:', filtered.length)
  filteredArticles.value = filtered
}

const handleBack = () => {
  router.push('/library')
}

const downloadLaw = () => {
  if (!law.value) return
  
  const blob = new Blob([law.value.content], { type: 'text/plain' })
  const url = window.URL.createObjectURL(blob)
  const a = window.document.createElement('a')
  a.href = url
  a.download = `${law.value.title}.txt`
  a.click()
  window.document.body.appendChild(a)
  window.document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

// Sidebar methods
const handleAISearch = async () => {
  if (!aiQuery.value.trim()) return
  
  isAILoading.value = true
  aiResponse.value = ''
  
  try {
    const query = `Regarding ${law.value?.title || 'this law'}, ${aiQuery.value}`
    const response = await apiService.searchLaws(query, {
      country_code: law.value?.countryCode || '',
      search_type: 'semantic'
    })
    
    if (response.response) {
      aiResponse.value = response.response
    } else if (response.results && response.results.length > 0) {
      aiResponse.value = `Found ${response.results.length} relevant results. The most relevant article is: ${response.results[0].article_content}`
    } else {
      aiResponse.value = 'No relevant information found. Please try rephrasing your question.'
    }
  } catch (error: any) {
    console.error('AI Search Error:', error)
    aiResponse.value = `Error: ${error.message || 'Failed to get AI response. Please try again.'}`
  } finally {
    isAILoading.value = false
  }
}

const clearAIResponse = () => {
  aiResponse.value = ''
  aiQuery.value = ''
}

const applyFilters = () => {
  updateFilteredArticles()
}

const clearFilters = () => {
  selectedCategory.value = ''
  articleRangeStart.value = ''
  articleRangeEnd.value = ''
  contentKeywords.value = ''
  sortBy.value = 'articleNumber'
  updateFilteredArticles()
}

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

// Close sidebar when clicking outside on mobile
const handleOutsideClick = (event: MouseEvent) => {
  if (window.innerWidth < 1024 && sidebarOpen.value) {
    const target = event.target as HTMLElement
    if (!target.closest('.sidebar')) {
      sidebarOpen.value = false
    }
  }
}

// Watchers
watch(() => law.value?.articles, () => {
  updateFilteredArticles()
}, { immediate: true })

watch(searchQuery, () => {
  updateFilteredArticles()
})

// Lifecycle
onMounted(() => {
  fetchLawDetails()
  document.addEventListener('click', handleOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
})
</script>
